import React, { useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { Session } from './utils/session';
import Sidebar from './Sidebar';
import StatCard from './StatCard';

const authHeaders = () => {
  const token = Session.getToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

const StartStopActivity = () => {
  // compute default date strings so inputs are controlled from first render
  const todayStr = new Date().toISOString().split('T')[0];
  // default start date: 2 months before today
  const minStartDefault = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 2);
    return d.toISOString().split('T')[0];
  })();
  // default start date shown to user: 7 days before today (but not older than minStartDefault)
  const sevenDaysAgoDefault = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    const s = d.toISOString().split('T')[0];
    return s < minStartDefault ? minStartDefault : s;
  })();
  // restrict start date to today (no future selection)
  const startMaxDefault = todayStr;
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [employeeName, setEmployeeName] = useState('');
  // Buffer for user selection so changing the select doesn't apply filter
  // until the Search button is clicked (same behavior as date inputs).
  const [employeeInput, setEmployeeInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mode, setMode] = useState('daily');
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [statCards, setStatCards] = useState([]);

  
  // initialize applied date range and separate input buffers so
  // selecting dates does not apply filtering until Search is clicked
  const [startDate, setStartDate] = useState(sevenDaysAgoDefault);
  const [endDate, setEndDate] = useState(todayStr);
  const [startInput, setStartInput] = useState(sevenDaysAgoDefault);
  const [endInput, setEndInput] = useState(todayStr);

  const toggleSidebar = useCallback(() => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar((s) => !s);
    }
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) {
        setShowMobileSidebar(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const fetchList = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/StartStop/Start_Stop_ALL`,
          {
            method: 'GET',
            headers: authHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to load activity list');
        }

        const data = await response.json();
        setActivity(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
        setActivity([]);
      } finally {
        setLoading(false);
      }
    };

    fetchList();
  }, []);

  useEffect(() => {
    setWorkspaces([
      { workspaceID: '1', workspaceName: 'Workspace 1' },
      { workspaceID: '2', workspaceName: 'Workspace 2' },
    ]);
  
  }, [activity, employeeName, mode]);

  const filteredActivity = activity.filter((item) => {
    const search = employeeName.trim();
    const username = (item.userName || '');

    // Only use the applied filter values; input buffers should not affect
    // filtering until the user clicks Search which copies them into
    // `startDate` / `endDate`.
    const effectiveStart = startDate || null;
    const effectiveEnd = endDate || null;

    if (!effectiveStart && !effectiveEnd) {
      if (mode === 'weekly') {
        const now = new Date();
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 6);
        const itemDate = item.startTime ? new Date(item.startTime) : null;
        if (!itemDate || itemDate < sevenDaysAgo || itemDate > now) {
          return false;
        }
      } else {
        return false;
      }
    }

    if (!effectiveStart || !effectiveEnd) {
      return false;
    }

    const itemDate = item.startTime ? new Date(item.startTime) : null;
    if (!itemDate) return false;
    const itemDateStr = itemDate.toISOString().split('T')[0];
    if (itemDateStr < effectiveStart) return false;
    if (itemDateStr > effectiveEnd) return false;

    // No `selectedDate` filtering (user requested removal)
    if (search === '') return true;
    return username.toLowerCase().includes(search.toLowerCase());
  });

  const formatMinutes = (m) => {
    const mins = Number(m);
    if (isNaN(mins)) return '';
    if (mins <= 0) return '0 min';
    const hours = Math.floor(mins / 60);
    const rem = mins % 60;
    if (hours > 0) {
      return rem > 0 ? `${hours}h ${rem}min` : `${hours}h`;
    }
    return `${rem} min`;
  };

  const getLast7Days = () => {
    const days = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const value = d.toISOString().split('T')[0];
      const label = d.toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
      days.push({ value, label });
    }
    return days;
  };

  const last7Days = getLast7Days();

  // Helper/date-range utilities for datepickers
  const formatDate = (d) => (d instanceof Date ? d.toISOString().split('T')[0] : '');
  const minStartStr = minStartDefault;

  // end date may be today; compute max as start + 2 months (capped at today)
  const computeEndMax = (startVal) => {
    if (!startVal) return todayStr;
    const d = new Date(startVal);
    d.setMonth(d.getMonth() + 2);
    const maxStr = d.toISOString().split('T')[0];
    return maxStr > todayStr ? todayStr : maxStr;
  };

  if (loading) return <div>Loading activity...</div>;
  if (error) return <div>Error: {error}</div>;
 
  return (
    <div className="app-wrapper">
      <div
        id="sidebar-overlay"
        className="sidebar-overlay"
        onClick={() => setShowMobileSidebar(false)}
      />

      <div id="sidebar" className={showMobileSidebar ? 'open' : ''}>
        <Sidebar />
      </div>

      <div id="content">
        {/* <div className="d-flex align-items-center mb-3">
          <button className="btn btn-toggle me-3" onClick={toggleSidebar}>
            <i className="fas fa-bars" />
          </button>
          <a href="/dashboard" className="text-muted text-decoration-none small">
            <i className="fas fa-chevron-left" /> Back
          </a>
        </div> */}

        <h2 className="mb-4">Task Reports</h2>
        <div className="row g-3 mb-4 align-items-end">
          {/* <div className="col-xl-3">
            <div className="filter-box">
              <input
                type="radio"
                className="btn-check"
                id="daily"
                checked={mode === 'daily'}
                onChange={() => setMode('daily')}
              />
              <label className="btn btn-outline-custom px-3" htmlFor="daily">
                Daily
              </label>

              <input
                type="radio"
                className="btn-check"
                id="weekly"
                checked={mode === 'weekly'}
                onChange={() => setMode('weekly')}
              />
              <label className="btn btn-outline-custom px-3" htmlFor="weekly">
                Weekly
              </label>
            </div>
          </div> */}
          <div className="col-xl-3">
            <label className="small text-muted">User</label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <select
                  className="form-control"
                  value={employeeInput}
                  onChange={(e) => setEmployeeInput(e.target.value)}
                >
                  <option value="">All Users</option>
                  {[...new Set(activity.map((a) => a.userName).filter(Boolean))].map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="col-xl-3">
            <label className="small text-muted">Start Date</label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="date"
                  className="form-control"
                  value={startInput}
                  min={minStartStr}
                  max={startMaxDefault}
                  onChange={(e) => {
                    let v = e.target.value;
                    if (v && v < minStartStr) v = minStartStr;
                    setStartInput(v || '');
                    // Do NOT apply startDate here; apply when Search button is clicked.
                    // If end input is before the new start, move the end INPUT to match.
                    if (endInput && v && endInput < v) {
                      setEndInput(v);
                    }
                  }}
                  style={{ flex: 1 }}
                />

              {/* <button
                type="button"
                className="btn btn-sm btn-primary ms-2"
                onClick={() => {
                  if (startDate) {
                    setEndDate(startDate);
                  } else {
                    setStartDate(todayStr);
                    setEndDate(todayStr);
                  }
                }}
              >
                Search
              </button> */}
            </div>
          </div>

          <div className="col-xl-3">
            {/* <label className="small text-muted">End Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate || todayStr}
              min={todayStr}
              max={todayStr}
              onChange={() => setEndDate(todayStr)}
            /> */}
            <label className="small text-muted">End Date</label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="date"
                className="form-control"
                value={endInput}
                min={startInput || minStartStr}
                max={computeEndMax(startInput)}
                onChange={(e) => {
                  const v = e.target.value;
                  setEndInput(v || '');
                  // Do not apply end date here; apply only when Search button is clicked.
                }}
                style={{ flex: 1 }}
              />
               <button
                type="button"
                className="btn btn-sm btn-primary ms-2"
                onClick={() => {
                  // Apply the current input values as the active filter.
                  const e = endInput || '';
                  const s = startInput || '';
                  setStartInput(s);
                  setStartDate(s || null);
                  setEndInput(e);
                  setEndDate(e || null);
                  // Also apply the employee/user selection buffer.
                  setEmployeeName(employeeInput || '');
                }}
              >
                Search
              </button>
            </div>
          </div>

          {/* removed selectedDate input as requested */}
        </div>
        {/* Table */}
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>User Name</th>
              <th>Task Name</th>
               <th>Start Date</th>
              <th>Total Time</th>
              
            </tr>
          </thead>
          <tbody>
            {filteredActivity.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center' }}>No user found</td>
              </tr>
            ) : (
              filteredActivity.map((item, idx) => {
                let totalMinutes = '';
                if (item.startTime && item.stopTime) {
                  const start = new Date(item.startTime);
                  const stop = new Date(item.stopTime);
                  const diffMs = stop - start;
                  if (!isNaN(diffMs) && diffMs > 0) {
                    let mins = Math.floor(diffMs / 60000);
                    if (mins === 0 && diffMs > 0) mins = 1;
                    totalMinutes = mins;
                  } else if (!isNaN(diffMs) && diffMs === 0) {
                    totalMinutes = 0;
                  }
                }
                const startDateDisplay = item.startTime
                  ? new Date(item.startTime).toLocaleDateString()
                  : '';
                return (
                  <tr key={item.id || idx}>
                    <td>{item.userName}</td>
                    <td>{item.itemName}</td>
                    <td>{startDateDisplay}</td>
                    <td>{totalMinutes !== '' ? formatMinutes(totalMinutes) : ''}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StartStopActivity;
